import { Box, Button, FormControl, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { alpha, useTheme } from '@mui/material/styles';

import GlobalDialog from '../../components/@extended/GlobalDialog';
import LoadingButton from '../../components/@extended/LoadingButton';
import { CopierAddTradingHoursDialogProps } from '../../types/copier';
import { ReactComponent as ArrowDropDownIcon } from '../../assets/images/icons/angle-down.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/check.svg';
import { sizeByBreakpoint, textSizeByBreakpoint } from '../../utils/helper';
import { ReactComponent as TrashIcon } from '../../assets/images/icons/trash6.svg';
import useAuth from '../../hooks/useAuth';
import { useTradeClient } from '../../client/client-hook';
import { useDispatch } from '../../store';
import { DispatchSnackbarService } from '../../services';
import { TradingHour, UpdateSlaveReq } from '../../proto/trade-copier_pb';
import { filter_type_map_value, risk_type_map_value } from './CopyTradingTableSection';

const service = new DispatchSnackbarService();

const CopierAddTradingHoursDialog = ({ isOpen, onClose, selectedSlave, selectedMaster, onSuccess }: CopierAddTradingHoursDialogProps) => {
  const theme = useTheme();
  const { user } = useAuth();
  const tradeClient = useTradeClient();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const dayToNumber: Record<string, number> = {
    Monday: 0,
    Tuesday: 1,
    Wednesday: 2,
    Thursday: 3,
    Friday: 4,
    Saturday: 5,
    Sunday: 6
  };

  type TradingHourRow = {
    day: string;
    startTime: string;
    endTime: string;
  };

  const [tradingHours, setTradingHours] = useState<TradingHourRow[]>([]);

  // Convert UTC trading hours to local time for display
  const utcTradingTimeToLocal = (utcHours: TradingHour.AsObject[], userTimezone: string = 'UTC'): TradingHourRow[] => {
    const offsetSec = getTimezoneOffset(userTimezone) * 60;
    const numberToDay: Record<number, string> = {
      0: 'Monday',
      1: 'Tuesday',
      2: 'Wednesday',
      3: 'Thursday',
      4: 'Friday',
      5: 'Saturday',
      6: 'Sunday'
    };

    const result: TradingHourRow[] = [];
    const grouped: Map<number, { start: number; end: number }[]> = new Map();

    // Group by day and collect time ranges
    utcHours.forEach((hour) => {
      const day = hour.dayOfWeek;
      if (!grouped.has(day)) {
        grouped.set(day, []);
      }
      grouped.get(day)!.push({ start: hour.startTime, end: hour.endTime });
    });

    // Convert each group to local time
    grouped.forEach((ranges, day) => {
      ranges.forEach((range) => {
        const localStart = range.start + offsetSec;
        const localEnd = range.end + offsetSec;

        const localStartDay = getAdjustedDay(day, localStart);

        const startSec = normalizeTime(localStart);
        const endSec = normalizeTime(localEnd);

        const startHours = Math.floor(startSec / 3600);
        const startMins = Math.floor((startSec % 3600) / 60);
        const endHours = Math.floor(endSec / 3600);
        const endMins = Math.floor((endSec % 3600) / 60);

        const startTime = `${String(startHours).padStart(2, '0')}:${String(startMins).padStart(2, '0')}`;
        const endTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;

        result.push({
          day: numberToDay[localStartDay] || 'Monday',
          startTime,
          endTime
        });
      });
    });

    return result;
  };

  // Load existing trading hours when dialog opens
  useEffect(() => {
    if (isOpen && selectedSlave?.slaveData?.tradingHoursList) {
      const existingHours = selectedSlave.slaveData.tradingHoursList;
      if (existingHours.length > 0) {
        const localHours = utcTradingTimeToLocal(existingHours, user?.timezone || 'UTC');
        setTradingHours(localHours);
      } else {
        setTradingHours([]);
      }
    } else if (isOpen) {
      setTradingHours([]);
    }
  }, [isOpen, selectedSlave]);

  const getTimezoneOffset = (timezone: string) => {
    if (!timezone || timezone === 'UTC') {
      return 0;
    }
    const now = new Date();
    const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;

    const tzDateString = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    }).format(now);

    const tzDate = new Date(tzDateString);
    const diff = (tzDate.getTime() - utcTime) / 60000;
    return Math.round(diff);
  };

  const normalizeTime = (timeSec: number) => ((timeSec % 86400) + 86400) % 86400;

  const getAdjustedDay = (originalDay: number, utcSeconds: number): number => (originalDay + Math.floor(utcSeconds / 86400) + 7) % 7;

  const localTradingTimeToUTCTradingTime = (hour: TradingHourRow, userTimezone: string = 'UTC'): TradingHour.AsObject[] => {
    const [sHours, sMinutes] = hour.startTime.split(':').map(Number);
    const [eHours, eMinutes] = hour.endTime.split(':').map(Number);
    const offsetSec = getTimezoneOffset(userTimezone) * 60;

    const sLocalSec = sHours * 3600 + sMinutes * 60;
    const eLocalSec = eHours * 3600 + eMinutes * 60;

    const sUTC = sLocalSec - offsetSec;
    const eUTC = eLocalSec - offsetSec;

    const dayOfWeek = dayToNumber[hour.day];
    const sDay = getAdjustedDay(dayOfWeek, sUTC);
    const eDay = getAdjustedDay(dayOfWeek, eUTC);

    const sSecondUTC = normalizeTime(sUTC);
    const eSecondUTC = normalizeTime(eUTC);

    if (sDay === eDay && sSecondUTC < eSecondUTC) {
      return [
        {
          dayOfWeek: sDay as any,
          startTime: sSecondUTC,
          endTime: eSecondUTC
        }
      ];
    } else if (sDay !== eDay) {
      return [
        {
          dayOfWeek: sDay as any,
          startTime: sSecondUTC,
          endTime: 86400
        },
        {
          dayOfWeek: eDay as any,
          startTime: 0,
          endTime: eSecondUTC
        }
      ];
    } else {
      return [
        {
          dayOfWeek: sDay as any,
          startTime: sSecondUTC,
          endTime: 86400
        },
        {
          dayOfWeek: sDay as any,
          startTime: 0,
          endTime: eSecondUTC
        }
      ];
    }
  };

  const handleSave = async () => {
    if (!user || !user.tcId || !selectedSlave || !selectedSlave.slaveData) {
      service.dispatchSnackbar(dispatch, 'Missing required information', 'alert', 'error', 5000);
      return;
    }

    // Validate trading hours
    for (const hour of tradingHours) {
      if (hour.startTime >= hour.endTime) {
        service.dispatchSnackbar(dispatch, 'End time must be after start time', 'alert', 'error', 5000);
        return;
      }
    }

    try {
      setLoading(true);
      const userTimezone = user.timezone || 'UTC';
      const tradingHoursList: TradingHour.AsObject[] = [];

      tradingHours.forEach((hour) => {
        const t = localTradingTimeToUTCTradingTime(hour, userTimezone)?.filter((x) => x.startTime !== x.endTime);
        tradingHoursList.push(...t);
      });

      const req: UpdateSlaveReq.AsObject = {
        userId: user.tcId,
        configId: selectedSlave.configId || '',
        slaveId: selectedSlave.id || '',
        enable: selectedSlave.slaveData.enabled,
        riskType: risk_type_map_value[selectedSlave.slaveData.riskType?.value || 'UNKNOWN'],
        lotMultiplierValue: selectedSlave.slaveData.lotMultiplierValue,
        fixedLotValue: selectedSlave.slaveData.fixedLotValue,
        copyTakeProfit: selectedSlave.slaveData.copyTakeProfit,
        copyStopLoss: selectedSlave.slaveData.copyStopLoss,
        copyPendingOrders: selectedSlave.slaveData.copyPendingOrders,
        forceMinimumLotSize: selectedSlave.slaveData.forceMinimumLotSize,
        forceMaximumLotSize: selectedSlave.slaveData.forceMaximumLotSize,
        copyComment: selectedSlave.slaveData.copyComment,
        customComment: selectedSlave.slaveData.customComment,
        reverseTrade: selectedSlave.slaveData.reverseTrade,
        strictClose: selectedSlave.slaveData.strictClose,
        considerContractSize: selectedSlave.slaveData.considerContractSize,
        copyMagicNumber: selectedSlave.slaveData.copyMagicNumber,
        magicNumber: selectedSlave.slaveData.magicNumber,
        lotRefinerValue: selectedSlave.slaveData.lotRefinerValue,
        magicFiltersList: selectedSlave.slaveData.magicFiltersList,
        copyExpiryTime: selectedSlave.slaveData.copyExpiryTime,
        commentFiltersList: selectedSlave.slaveData.commentFiltersList,
        minLotFilter: selectedSlave.slaveData.minLotFilter,
        maxLotFilter: selectedSlave.slaveData.maxLotFilter,
        stopLossPip: selectedSlave.slaveData.stopLossPip,
        takeProfitPip: selectedSlave.slaveData.takeProfitPip,
        slRefiner: selectedSlave.slaveData.slRefiner,
        tpRefiner: selectedSlave.slaveData.tpRefiner,
        manageExisting: selectedSlave.slaveData.manageExisting,
        allowedTradeSide: selectedSlave.slaveData.allowedTradeSide,
        copyExistingTrades: selectedSlave.slaveData.copyExistingTrades,
        balanceMultiplierValue: selectedSlave.slaveData.balanceMultiplierValue,
        balancePercentageValue: selectedSlave.slaveData.balancePercentageValue,
        fixedBalanceMultiplierValue: selectedSlave.slaveData.fixedBalanceMultiplierValue,
        fixedMasterBalance: selectedSlave.slaveData.fixedMasterBalance,
        maxDelayTime: selectedSlave.slaveData.maxDelayTime,
        minDelayTime: selectedSlave.slaveData.minDelayTime,
        tradingHoursList: tradingHoursList,
        magicFilterType: filter_type_map_value[selectedSlave.slaveData.magicFilterType?.value || 'UNKNOWN'],
        commentFilterType: filter_type_map_value[selectedSlave.slaveData.commentFilterType?.value || 'UNKNOWN']
      };

      const client = await tradeClient();
      await client.updateSlave(req);

      setLoading(false);
      service.dispatchSnackbar(dispatch, 'Trading hours updated successfully', 'alert', 'success', 5000);

      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      console.error('ERROR: ', err);
      setLoading(false);
      service.dispatchSnackbar(dispatch, err.message || 'Failed to update trading hours', 'alert', 'error', 5000);
    }
  };

  const inputStyle = {
    borderRadius: '6px',
    '& .MuiOutlinedInput-root': {
      boxShadow: 'none !important',
      '& fieldset': {
        border: 'none'
      },
      '&:hover fieldset': {
        border: 'none'
      },
      '&.Mui-focused fieldset': {
        border: 'none'
      },
      '& input': {
        boxShadow: 'none',
        outline: 'none',
        fontWeight: 500,
        fontSize: sizeByBreakpoint(1, 16),
        cursor: 'pointer',
        accentColor: theme.palette.primary.main,
        colorScheme: 'light',
        '&::-webkit-datetime-edit-hour-field:focus': {
          backgroundColor: theme.palette.primary.main,
          color: 'white'
        },
        '&::-webkit-datetime-edit-minute-field:focus': {
          backgroundColor: theme.palette.primary.main,
          color: 'white'
        }
      }
    },
    '& input[type="time"]::-webkit-calendar-picker-indicator': {
      cursor: 'pointer',
      fontSize: '18px',
      padding: '4px',
      marginLeft: '4px',
      filter: `opacity(0.7)`
    }
  };

  const formBoxStyle = {
    width: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    px: 1.5,
    py: 0.5,
    cursor: 'pointer',
    height: sizeByBreakpoint(2, 44),
    borderWidth: 1,
    borderColor: theme.palette.border.main + '!important',
    borderRadius: '10px',
    '&:focus-within': {
      borderColor: theme.palette.primary.main
    },
    border: '1px solid',
    backgroundColor: theme.palette.background.paper,
    fontSize: sizeByBreakpoint(1, 16)
  };

  return (
    <GlobalDialog
      open={isOpen}
      onClose={onClose}
      title={`Trading Hours (${user?.timezone || 'UTC'})`}
      subtitle="Showing the list of all the accounts."
      footer={
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <LoadingButton
            sx={{
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: sizeByBreakpoint(1, 16),
              height: sizeByBreakpoint(1, 40)
            }}
            loadingPosition="end"
            startIcon={<ClockCircleOutlined style={{ fontSize: '18px' }} />}
            loading={false}
            disableElevation
            disabled={false}
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setTradingHours([...tradingHours, { day: days[0], startTime: '00:00', endTime: '00:00' }]);
            }}
          >
            Add Trading Hours
          </LoadingButton>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              sx={{
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: sizeByBreakpoint(1, 16),
                height: sizeByBreakpoint(1, 40),
                color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.secondary),
                borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]),
                '&:hover': {
                  borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.grey[500]),
                  backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50])
                }
              }}
              disableElevation
              variant="outlined"
              color="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>

            <LoadingButton
              sx={{
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: sizeByBreakpoint(1, 16),
                height: sizeByBreakpoint(1, 40)
              }}
              loadingPosition="end"
              startIcon={<CheckIcon />}
              loading={loading}
              disableElevation
              disabled={loading}
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSave}
            >
              Confirm
            </LoadingButton>
          </Box>
        </Box>
      }
      maxWidth="md"
    >
      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.warning.lighter, 0.4),
          border: theme.palette.mode === 'dark' ? `1px solid ${alpha(theme.palette.warning.main, 0.3)}` : 'none',

          p: sizeByBreakpoint(1, 15),
          borderRadius: '10px',
          mb: sizeByBreakpoint(1, 20)
        }}
      >
        <Typography
          variant="body2"
          color={theme.palette.mode === 'dark' ? theme.palette.warning.light : alpha(theme.palette.warning.main, 0.8)}
          sx={{
            fontSize: sizeByBreakpoint(1, 14)
          }}
          fontWeight={500}
        >
          Configure the trading hours that you wish to copy trades, any trades placed outside of this time will be ignored. You can
          configure the same day more than once to have multiple trading times per day.
        </Typography>
      </Box>
      {tradingHours.map((hour, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: sizeByBreakpoint(1, 15)
          }}
        >
          <Box
            sx={{
              width: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: sizeByBreakpoint(1, 15)
            }}
          >
            <FormControl sx={{ flex: 1, minWidth: '230px' }} variant="outlined" size="small">
              <Box sx={formBoxStyle}>
                <CalendarOutlined width="20px" height="20px" color={theme.palette.primary.main} />

                <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }} fontSize={textSizeByBreakpoint(16)}>
                  Day:
                </Typography>

                <Select
                  value={hour.day}
                  onChange={(e) => {
                    const newHours = [...tradingHours];
                    newHours[index].day = e.target.value;
                    setTradingHours(newHours);
                  }}
                  displayEmpty
                  IconComponent={ArrowDropDownIcon}
                  sx={{
                    padding: '0 !important',
                    height: '100%',
                    fontSize: sizeByBreakpoint(1, 16),
                    fontWeight: 500,
                    width: '100%',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.MuiOutlinedInput-root': {
                      boxShadow: 'none',
                      outline: 'none'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiSelect-icon': {
                      marginTop: '-2px'
                    }
                  }}
                >
                  {days.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: '230px' }} variant="outlined" size="small">
              <Box
                sx={formBoxStyle}
                onClick={() => {
                  const input = document.querySelector(`#start-time-${index}`) as HTMLInputElement;
                  if (input && 'showPicker' in input) (input as any).showPicker();
                }}
              >
                <ClockCircleOutlined width="20px" height="20px" color={theme.palette.primary.main} />

                <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }} fontSize={textSizeByBreakpoint(16)}>
                  Time:
                </Typography>

                <TextField
                  id={`start-time-${index}`}
                  sx={inputStyle}
                  type="time"
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    style: {
                      accentColor: theme.palette.primary.main
                    }
                  }}
                  value={hour.startTime}
                  onChange={(e) => {
                    const newHours = [...tradingHours];
                    newHours[index].startTime = e.target.value;
                    setTradingHours(newHours);
                  }}
                  fullWidth
                />
              </Box>
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: '230px' }} variant="outlined" size="small">
              <Box
                sx={formBoxStyle}
                onClick={() => {
                  const input = document.querySelector(`#end-time-${index}`) as HTMLInputElement;
                  if (input && 'showPicker' in input) (input as any).showPicker();
                }}
              >
                <ClockCircleOutlined width="20px" height="20px" color={theme.palette.primary.main} />

                <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }} fontSize={textSizeByBreakpoint(16)}>
                  Time:
                </Typography>

                <TextField
                  id={`end-time-${index}`}
                  sx={inputStyle}
                  type="time"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    style: {
                      accentColor: theme.palette.primary.main
                    }
                  }}
                  value={hour.endTime}
                  onChange={(e) => {
                    const newHours = [...tradingHours];
                    newHours[index].endTime = e.target.value;
                    setTradingHours(newHours);
                  }}
                  fullWidth
                />
              </Box>
            </FormControl>
          </Box>
          <Button
            color="error"
            variant="outlined"
            size="small"
            onClick={() => {
              const newHours = tradingHours.filter((_, i) => i !== index);
              setTradingHours(newHours);
            }}
            sx={{
              minWidth: 'max-content',
              width: sizeByBreakpoint(2, 44),
              height: sizeByBreakpoint(2, 44),
              fontSize: sizeByBreakpoint(1.5, 18)
            }}
          >
            <Box
              component={TrashIcon}
              sx={{
                fontSize: sizeByBreakpoint(1, 16),
                '& path': {
                  fill: theme.palette.error.main
                }
              }}
            />
          </Button>
        </Box>
      ))}
    </GlobalDialog>
  );
};

export default CopierAddTradingHoursDialog;
